import React from 'react';
import { Link } from 'react-router-dom';
import { AppConfig, PLATFORM_LABELS } from '../../types';
import { useI18n } from '../../context/I18nContext';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { StoreBadge } from '../ui/StoreBadge';

export interface AppCardProps {
  app: AppConfig;
  showStoreBadges?: boolean;
}

export const AppCard: React.FC<AppCardProps> = ({ app, showStoreBadges = true }) => {
  const { resolveText, t } = useI18n();

  return (
    <div className="bg-white border border-slate-200/90 rounded-2xl p-6 sm:p-8 shadow-xs hover:shadow-sm hover:border-slate-300/90 transition-all duration-200 flex flex-col justify-between group">
      <div>
        {/* Top: Icon + Title + Platforms */}
        <div className="flex items-start gap-4 sm:gap-5">
          <img
            src={app.iconUrl}
            alt={`${app.name} Icon`}
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover shadow-xs border border-slate-100 shrink-0"
            loading="lazy"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge variant="brand" size="sm">
                {resolveText(app.category)}
              </Badge>
              {app.platforms.map((p) => (
                <Badge key={p} variant="platform" size="sm">
                  {PLATFORM_LABELS[p] || p}
                </Badge>
              ))}
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight group-hover:text-primary-700 transition-colors truncate">
              {app.name}
            </h3>
            <p className="text-xs text-slate-400 font-mono mt-0.5">
              {app.currentVersion} • Updated {app.lastUpdated}
            </p>
          </div>
        </div>

        {/* Tagline & Description */}
        <div className="mt-4 sm:mt-5 space-y-2">
          <p className="text-base font-semibold text-slate-800 leading-snug">
            {resolveText(app.tagline)}
          </p>
          <p className="text-sm text-slate-600 line-clamp-3 leading-relaxed">
            {resolveText(app.description)}
          </p>
        </div>

        {/* Feature Highlights (first 2) */}
        {app.features && app.features.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col gap-1.5">
            {app.features.slice(0, 2).map((feat) => (
              <div key={feat.id} className="flex items-center gap-2 text-xs text-slate-600">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0" />
                <span className="font-medium text-slate-700">{resolveText(feat.title)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom CTA Row */}
      <div className="mt-6 pt-5 border-t border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <Link to={`/apps/${app.slug}`} className="sm:order-1">
          <Button variant="secondary" size="md" fullWidth>
            {t.nav.viewDetails}
          </Button>
        </Link>

        {showStoreBadges && (
          <div className="flex items-center gap-2 sm:order-2 flex-wrap">
            {app.appStoreUrl && <StoreBadge platform="ios" url={app.appStoreUrl} size="md" />}
            {app.googlePlayUrl && <StoreBadge platform="android" url={app.googlePlayUrl} size="md" />}
          </div>
        )}
      </div>
    </div>
  );
};
